import React, { useState, useContext } from 'react';
import { 
  Box, 
  AppBar as MuiAppBar, 
  Toolbar, 
  List, 
  Typography, 
  IconButton, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Drawer as MuiDrawer,
  useTheme,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import BackgroundLayout from './BackgroundLayout';
import SessionTimer from './SessionTimer';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  AddBox as AddBoxIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  MonetizationOn as MonetizationOnIcon,
  Flag as GoalsIcon,
  PersonAdd as LeadsIcon,
  Group as ClientsIcon,
  Receipt as TransactionsIcon,
  ContactPhone as ContactsIcon,
  Business as CrmIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as OrdersIcon,
  Build as WorkshopIcon,
  BugReport as BugReportIcon,
  LocalOffer as SalesIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon
} from '@mui/icons-material';
import { useSpring, animated, config } from '@react-spring/web';

const drawerWidth = 280;
const closedDrawerWidth = 72;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: `calc(100% - ${open ? drawerWidth : closedDrawerWidth}px)`,
  marginLeft: open ? drawerWidth : closedDrawerWidth,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : closedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    width: drawerWidth,
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
  ...(!open && {
    width: closedDrawerWidth,
    '& .MuiDrawer-paper': {
      width: closedDrawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  }),
}));

const menuItems = [
  { title: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { title: 'Lista Produktów', icon: <InventoryIcon />, path: '/products' },
  { title: 'Dodaj Produkt', icon: <AddBoxIcon />, path: '/add-product', adminOnly: true },
  { title: 'Transfery Produktów', icon: <SwapHorizIcon />, path: '/transfers' },
  { title: 'Zamówienia w trakcie', icon: <OrdersIcon />, path: '/orders' },
  { title: 'Towar do pracowni', icon: <WorkshopIcon />, path: '/workshop' },
  { title: 'Historia/Logi', icon: <HistoryIcon />, path: '/history', adminOnly: true },
  { title: 'Grafik', icon: <ScheduleIcon />, path: '/schedule' },
  { title: 'Lista płac', icon: <MonetizationOnIcon />, path: '/payroll', adminOnly: true },
  { title: 'Użytkownicy', icon: <PeopleIcon />, path: '/users', adminOnly: true },
  { title: 'Zgłoszenia błędów', icon: <BugReportIcon />, path: '/error-reports', adminOnly: true },
  { title: 'Ustawienia', icon: <SettingsIcon />, path: '/settings', adminOnly: true },
];

const crmMenuItems = [
  { 
    title: 'Nowe Leady', 
    icon: <LeadsIcon />, 
    path: '/leads', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Nowe Leady',
    userTitle: 'Moje Leady'
  },
  { 
    title: 'Klienci', 
    icon: <ClientsIcon />, 
    path: '/clients', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Klienci',
    userTitle: 'Moi Klienci'
  },
  { 
    title: 'Sprzedaże CRM', 
    icon: <TransactionsIcon />, 
    path: '/sales', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Sprzedaże CRM',
    userTitle: 'Moje Sprzedaże'
  },
  { 
    title: 'Kontakty', 
    icon: <ContactsIcon />, 
    path: '/contacts', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Kontakty',
    userTitle: 'Moje Kontakty'
  },
  { 
    title: 'Zadania', 
    icon: <AssignmentIcon />, 
    path: '/tasks', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Zadania',
    userTitle: 'Moje Zadania'
  },
  { 
    title: 'Cele Sprzedażowe', 
    icon: <EmojiEventsIcon />, 
    path: '/goals', 
    displayForAdmin: true,
    displayForUser: true,
    adminTitle: 'Cele Sprzedażowe',
    userTitle: 'Moje Cele'
  },
];

const Layout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, logout } = useContext(AuthContext);
  const [open, setOpen] = useState(true);
  const [crmOpen, setCrmOpen] = useState(false);
  const [reportBugDialogOpen, setReportBugDialogOpen] = useState(false);
  const [bugReport, setBugReport] = useState({ description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCrmClick = () => {
    setCrmOpen(!crmOpen);
  };

  const handleReportBugClick = () => {
    setReportBugDialogOpen(true);
    // Resetowanie formularza przy otwarciu
    setBugReport({ description: '' });
  };
  
  const handleBugReportChange = (e) => {
    setBugReport({
      ...bugReport,
      [e.target.name]: e.target.value
    });
  };
  
  const handleBugReportClose = () => {
    setReportBugDialogOpen(false);
  };
  
  const handleBugReportSubmit = async () => {
    try {
      // Pobieranie informacji o użytkowniku i aktualnej stronie
      const currentPath = location.pathname;
      const timestamp = new Date().toISOString();
      const userId = currentUser?.uid || 'anonymous';
      const userName = currentUser?.displayName || 'Nieznany użytkownik';
      
      // Tworzenie obiektu zgłoszenia
      const reportData = {
        description: bugReport.description,
        userId,
        userName,
        path: currentPath,
        timestamp,
        status: 'new',
        browser: navigator.userAgent
      };
      
      // Zapisywanie do Firebase
      const db = getFirestore();
      await addDoc(collection(db, 'bugReports'), reportData);
      
      // Zamykanie dialogu i wyświetlanie powiadomienia
      setReportBugDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Zgłoszenie zostało wysłane. Dziękujemy!',
        severity: 'success'
      });
      
      // Resetowanie formularza
      setBugReport({ description: '' });
    } catch (error) {
      console.error('Błąd podczas wysyłania zgłoszenia:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas wysyłania zgłoszenia.',
        severity: 'error'
      });
    }
  };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };



  // Animacje wejścia
  const fadeIn = useSpring({
    from: { 
      opacity: 0,
      transform: 'scale(0.9)'
    },
    to: { 
      opacity: 1,
      transform: 'scale(1)'
    },
    config: {
      tension: 280,
      friction: 20
    },
  });

  const slideInLeft = useSpring({
    from: { 
      opacity: 0,
      transform: 'translateX(-20px)' 
    },
    to: { 
      opacity: 1,
      transform: 'translateX(0)' 
    },
    config: config.gentle,
  });

  const slideInTop = useSpring({
    from: { 
      opacity: 0,
      transform: 'translateY(-20px)' 
    },
    to: { 
      opacity: 1,
      transform: 'translateY(0)' 
    },
    config: config.gentle,
  });

  return (
    <BackgroundLayout>
      {/* Dialog do zgłaszania błędów */}
      <Dialog open={reportBugDialogOpen} onClose={handleBugReportClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 'bold' }}>
          Zgłoś błąd
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper', pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Opisz napotkany problem. Twoje zgłoszenie pomoże nam poprawić działanie systemu.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            name="description"
            label="Opis błędu"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={bugReport.description}
            onChange={handleBugReportChange}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            Zgłoszenie będzie zawierać informację o aktualnej stronie ({location.pathname}) oraz dane przeglądarki.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper', px: 3, pb: 2 }}>
          <Button onClick={handleBugReportClose} color="inherit">
            Anuluj
          </Button>
          <Button 
            onClick={handleBugReportSubmit} 
            variant="contained" 
            color="primary"
            disabled={!bugReport.description.trim()}
          >
            Wyślij zgłoszenie
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar do wyświetlania powiadomień */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar position="fixed" open={open}>
          <animated.div style={slideInTop}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="toggle drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={{ 
                  marginRight: '36px',
                  ...(open && { transform: 'rotate(180deg)' }),
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.standard,
                  }),
                }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/logocloud.png" 
                  alt="M&M Gold Cloud Logo" 
                  style={{ 
                    height: '40px',
                    width: '40px',
                    marginRight: '12px',
                    objectFit: 'contain',
                    borderRadius: '50%',
                    padding: '4px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(5px)'
                  }} 
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontFamily: 'Cinzel, serif',
                    color: 'white',
                    textShadow: '0 0 10px rgba(255,255,255,0.3)'
                  }}
                >
                  M&M GOLD Cloud
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="subtitle2" sx={{ mr: 2 }}>
                {currentUser?.firstName} {currentUser?.lastName}
              </Typography>
              <SessionTimer />
              <Tooltip title="Wyloguj">
                <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </animated.div>
        </AppBar>

        <Drawer variant="permanent" open={open}>
          <animated.div style={slideInLeft}>
            <Toolbar />
            <List>
              {menuItems
                .filter(item => {
                  if (item.title === 'Dodaj Produkt') {
                    // Pokaż "Dodaj Produkt" dla admin i marketing
                    return userRole === 'admin' || userRole === 'marketing';
                  }
                  if (item.displayForAdmin && item.displayForUser) {
                    // Pokaż "Sprzedaże" dla admin i zwykłych użytkowników
                    return (userRole === 'admin' && item.adminTitle) || (userRole !== 'admin' && item.userTitle);
                  }
                  // Standardowe filtrowanie dla pozostałych elementów
                  return !item.adminOnly || userRole === 'admin';
                })
                .map((item) => (
                  <ListItem
                    button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    selected={location.pathname === item.path}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      borderRadius: '0 24px 24px 0',
                      mr: 1,
                      ...(location.pathname === item.path && {
                        backgroundColor: 'rgba(212, 175, 55, 0.1)',
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: location.pathname === item.path ? 'primary.main' : 'inherit'
                      }}
                    >
                      <Tooltip title={!open ? item.title : ''} placement="right">
                        {item.icon}
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        (item.displayForAdmin && item.displayForUser) 
                          ? (userRole === 'admin' ? item.adminTitle : item.userTitle) 
                          : item.title
                      } 
                      sx={{ 
                        opacity: open ? 1 : 0,
                        display: open ? 'block' : 'none',
                        color: location.pathname === item.path ? 'primary.main' : 'inherit',
                        '& .MuiListItemText-primary': {
                          fontWeight: location.pathname === item.path ? 600 : 400
                        }
                      }} 
                    />
                  </ListItem>
                ))}
              
              {/* CRM Menu Group - dostępne dla wszystkich */}
              <>
                <ListItem 
                  button 
                  onClick={handleCrmClick}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <CrmIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="CRM" 
                    sx={{ 
                      opacity: open ? 1 : 0,
                      display: open ? 'block' : 'none',
                    }} 
                  />
                  {open && (crmOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItem>
                <Collapse in={crmOpen && open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {crmMenuItems
                      .filter(item => {
                        if (item.title === 'Nowe Leady') {
                          return true;
                        }
                        if (item.title === 'Sprzedaże CRM') {
                          return true;
                        }
                        if (item.title === 'Kontakty') {
                          return true;
                        }
                        return !item.adminOnly || userRole === 'admin';
                      })
                      .map((item) => (
                        <ListItem
                          button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          selected={location.pathname === item.path}
                          sx={{ 
                            pl: 4,
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                          }}
                        >
                          <ListItemIcon 
                            sx={{
                              minWidth: 0,
                              mr: open ? 3 : 'auto',
                              justifyContent: 'center',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.title} 
                            sx={{ 
                              opacity: open ? 1 : 0,
                              display: open ? 'block' : 'none',
                            }} 
                          />
                        </ListItem>
                      ))}
                  </List>
                </Collapse>
              </>
              
              {/* Przycisk Zgłoś błąd na dole menu */}
              <Box sx={{ mt: 'auto', mb: 2, display: 'flex', justifyContent: 'center', width: '100%' }}>
                <ListItem
                  button
                  onClick={handleReportBugClick}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    borderRadius: 1,
                    mt: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: 'primary.main'
                    }}
                  >
                    <Tooltip title={!open ? 'Zgłoś błąd' : ''} placement="right">
                      <BugReportIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText
                    primary="Zgłoś błąd"
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? 'block' : 'none',
                    }}
                  />
                </ListItem>
              </Box>
            </List>
          </animated.div>
        </Drawer>

        <Box
          component="main"
          sx={{
            backgroundColor: 'transparent',
            flexGrow: 1,
            minHeight: '100vh',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            marginLeft: 0,
            p: 3,
            '@media (max-width: 600px)': {
              p: 1,
            },
          }}
        >
          <Toolbar />
          <animated.div style={fadeIn}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Outlet />
            </Box>
          </animated.div>
        </Box>
      </Box>
    </BackgroundLayout>
  );
};

export default Layout;
